import { useEffect, useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import styles from './FeedbackButton.module.css';

type Vote = 'up' | 'down' | null;

interface Props {
  scope: string; // unique key, e.g. `qna-${slug}-${qid}`
  label?: string;
  className?: string;
}

const STORE_KEY = 'tia.feedback';

function readAll(): Record<string, Vote> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, Vote>) : {};
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, Vote>) {
  localStorage.setItem(STORE_KEY, JSON.stringify(map));
}

export function FeedbackButton({ scope, label = 'Was this flag accurate?', className }: Props) {
  const [vote, setVote] = useState<Vote>(null);

  useEffect(() => {
    const all = readAll();
    setVote(all[scope] ?? null);
  }, [scope]);

  const onVote = (v: Vote) => {
    const next = vote === v ? null : v;
    setVote(next);
    const all = readAll();
    if (next == null) delete all[scope];
    else all[scope] = next;
    writeAll(all);
  };

  return (
    <span className={[styles.wrap, className].filter(Boolean).join(' ')} role="group" aria-label={label}>
      <span className={styles.label}>{label}</span>
      <button
        type="button"
        className={[styles.btn, vote === 'up' ? styles.up : ''].join(' ')}
        onClick={() => onVote('up')}
        aria-label="Accurate"
        aria-pressed={vote === 'up'}
      >
        <ThumbsUp size={12} strokeWidth={1.5} />
      </button>
      <button
        type="button"
        className={[styles.btn, vote === 'down' ? styles.down : ''].join(' ')}
        onClick={() => onVote('down')}
        aria-label="Inaccurate"
        aria-pressed={vote === 'down'}
      >
        <ThumbsDown size={12} strokeWidth={1.5} />
      </button>
    </span>
  );
}
